import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Toast } from "@/components/Toast";
import VerificationCodeInput from "@/components/Input/verifyCodeInput";
import {
  getPhoneVCode,
  forgotPassword,
  getEmailVCode,
  emailRegister,
  smsRegister,
} from "@/modules/login/api";
import { observer } from "mobx-react-lite";
import { loginStore } from "@/modules/login/store";
import { Box, Button, Flex, Input, Text } from "@chakra-ui/react";
import { themeStore } from "@/stores/theme";
import { isValidEmail, isValidPassword, isValidPhone, sensitiveWordCheck } from "@/utils/string";
import { langStore, translate } from "@noli/business";
import { EmailSubject } from "@/modules/login/constants";
import { Login_Words, Toast_Words } from "@/utils/lang";
import PasswordInput from "@/components/Input/password";
import uiConfig from "@/components/config";

const Register = () => {
  const { visible } = loginStore.loginState;
  const lang = langStore.tgt;
  const [account, setAccount] = useState("");
  const { loginBtnColor, loginBtnDisableColor, loginBtnTextColor } = themeStore.styles;
  const [password, setPassword] = useState("");
  const [nickName, setNickName] = useState("");
  const [vCode, setVCode] = useState("");
  const [loading, setLoading] = useState(false);

  const [prevGetVCodeTimestamp] = useState<string | null>(
    localStorage.getItem("prevForgetGetVCodeTimestamp"),
  );

  const getVerifyTranslate = useCallback(
    (time: number) => {
      return time > 0
        ? translate(Login_Words.codeDisable, lang).replace("$", String(time))
        : translate(Login_Words.getCode, lang);
    },
    [lang],
  );
  useEffect(() => {
    if (visible) setVCode("");
  }, [visible]);

  const accountVerification = useMemo(
    (): boolean => (lang === "zh" ? isValidPhone(account) : isValidEmail(account)),
    [account, lang],
  );
  const passwordVerification = useMemo((): boolean => isValidPassword(password), [password]);
  const vCodeVerification = useMemo((): boolean => {
    const reg = /^[0-9]\d{5}$/;
    return reg.test(vCode);
  }, [vCode]);

  const nickVerification = useMemo(() => nickName.length > 0 && nickName.length < 15, [nickName]);

  const loginEnable = useMemo(
    () => accountVerification && vCodeVerification && passwordVerification && nickVerification,
    [accountVerification, vCodeVerification, passwordVerification, nickVerification],
  );

  const handleAccountChange = (value: string) => {
    if (isValidEmail(value)) {
      loginStore?.setEmail(value);
    }

    if (isValidPhone(value)) {
      loginStore?.setPhone(value);
    }
    setAccount(value);
  };

  const onPasswordChange: React.ChangeEventHandler<{ value: string }> = (e) => {
    setPassword(e.target.value);
  };

  const onNickNameChange: React.ChangeEventHandler<{ value: string }> = (e) => {
    setNickName(e.target.value);
  };

  const handlerGetVCode = useCallback(() => {
    if (accountVerification) {
      localStorage.setItem("prevForgetGetVCodeTimestamp", `${new Date().getTime()}`);
      if (isValidPhone(account)) {
        getPhoneVCode({
          mobileNumber: account,
        }).catch((err: any) => {
          Toast({
            title: translate(Toast_Words.codeSendFail, lang),
            status: "error",
          });
        });
      } else {
        getEmailVCode({ email: account, subject: EmailSubject.Register }).catch((err: any) => {
          Toast({
            title: translate(Toast_Words.codeSendFail, lang),
            status: "error",
          });
        });
      }
    } else {
      Toast({
        title: translate(Toast_Words.accountInvalid, lang),
        status: "warning",
      });
    }

    return accountVerification;
  }, [accountVerification, account]);

  const handleRegister = () => {
    setLoading(true);
    const isPhoneMode = isValidPhone(account);
    sensitiveWordCheck(nickName)
      .then(() => {
        if (isPhoneMode) {
          smsRegister({
            mobileNumber: account,
            name: nickName,
            code: vCode,
            password,
          })
            .then((res: any) => {
              if (res.errCode == 0) {
                Toast({
                  title: translate(Toast_Words.registerSuccess, lang),
                  status: "success",
                });
                loginStore?.afterLogin({ token: res.token, expire: res.expire });
                loginStore?.closeLoginDialog();
              } else {
                Toast({
                  title: translate(Toast_Words.registerFailed, lang),
                  status: "error",
                });
              }
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          emailRegister({
            code: vCode,
            password: password,
            name: nickName,
            email: account,
          })
            .then((res: any) => {
              if (res.errCode == 0) {
                Toast({
                  title: translate(Toast_Words.registerSuccess, lang),
                  status: "success",
                });
                loginStore?.afterLogin({ token: res.token, expire: res.expire });
                loginStore?.closeLoginDialog();
              } else {
                Toast({
                  title: translate(Toast_Words.registerFailed, lang),
                  status: "error",
                });
              }
            })
            .finally(() => {
              setLoading(false);
            });
        }
      })
      .catch(() => {
        Toast({
          title: translate(Toast_Words.usernameSensitiveCheckFailed, lang),
          status: "warning",
        });
        setLoading(false);
      });
  };

  const switchToLogin = () => {
    loginStore.setDialogType("account");
  };

  return (
    <Box w="100%">
      <Text mt="16px" mb="8px" fontSize="14px" color={"#4E5969"}>
        {lang === "zh"
          ? translate(Login_Words.phoneNum, lang)
          : translate(Login_Words.emailAddr, lang)}
      </Text>
      <Input
        borderColor="fill.emphasis"
        color="inherit"
        _focusVisible={{ borderColor: loginBtnColor }}
        value={account}
        _placeholder={{ color: "#C9CDD4" }}
        fontSize={uiConfig.inputFontSize}
        onChange={(e: any) => handleAccountChange(e.target.value)}
        placeholder={
          lang === "zh"
            ? translate(Login_Words.phoneRegisterPhd, lang)
            : translate(Login_Words.emailPhd, lang)
        }
      />
      <Text mt="16px" mb="8px" fontSize="14px" color={"#4E5969"}>
        {translate(Login_Words.username, lang)}
      </Text>
      <Input
        borderColor="fill.emphasis"
        _focusVisible={{ borderColor: loginBtnColor }}
        value={nickName}
        _placeholder={{ color: "#C9CDD4" }}
        color="inherit"
        fontSize={uiConfig.inputFontSize}
        placeholder={translate(Login_Words.usernamePhd, lang)}
        onChange={onNickNameChange}
      />
      <Text mt="16px" mb="8px" fontSize="14px" color={"#4E5969"}>
        {translate(Login_Words.password, lang)}
      </Text>
      <PasswordInput
        _focusVisible={{ borderColor: loginBtnColor }}
        value={password}
        placeholder={translate(Login_Words.registerPasswordPhd, lang)}
        onChange={onPasswordChange}
      />
      <Text mt="16px" mb="8px" fontSize="14px" color={"#4E5969"}>
        {translate(Login_Words.code, lang)}
      </Text>
      <VerificationCodeInput
        btnText={getVerifyTranslate}
        value={vCode}
        onChange={(e: any) => {
          setVCode(e.target.value);
        }}
        length={6}
        placeholder={
          lang === "zh"
            ? translate(Login_Words.emailCodePhd, lang)
            : translate(Login_Words.emailCodePhd, lang)
        }
        onGetCode={handlerGetVCode}
        prevGetCodeTimestamp={prevGetVCodeTimestamp}
      />
      <Flex justifyContent="end" color={loginBtnColor} mt="1em">
        <Text cursor="pointer" onClick={switchToLogin} fontSize={"12px"}>
          {translate(Login_Words.loginExistAccount, lang)}
        </Text>
      </Flex>
      <Button
        type="button"
        size="md"
        w="100%"
        rounded="full"
        mt="40px"
        bgColor={loginBtnColor}
        color={loginBtnTextColor}
        _disabled={{
          bgColor: loginBtnDisableColor,
        }}
        _hover={{}}
        _focus={{}}
        _active={{}}
        style={{ fontWeight: 600, marginBottom: "21px", height: "40px" }}
        disabled={!loginEnable}
        loadingText={translate(Login_Words.register, lang) + "..."}
        onClick={handleRegister}
        isLoading={loading}
      >
        {translate(Login_Words.register, lang)}
      </Button>
    </Box>
  );
};

export default observer(Register);
